import { useAppSelector, useAppDispatch } from 'redux/hooks';
import { selectCurrentTab } from 'redux/slices/adminMenu';
import { Menus, IntroLot, Video, NotificationType } from 'constants/index';
import { selectLot, setCreatedLot } from 'redux/slices/lotCreation';
import { selectEditLot, setEditLot } from 'redux/slices/lotEdition';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VideoFormComponent } from './videoFormComponent';
import { Button } from 'views/buttons/base';
import { editIntroLotVideos } from 'services/lot/lotService';
import { setNotificationMessage, setNotificationType } from 'redux/slices/notification';
import './videoFormComponent.scss';

// Rows carry a client side id: uploads answer asynchronously and rows can be removed
// meanwhile, so a result is written to a row, never to a position.
export type VideoRow = Video & { rowId: number };

let lastRowId = 0;
const withRowId = (video: Video): VideoRow => ({ ...video, rowId: ++lastRowId });
const emptyVideo = (videoOrder: number): VideoRow => withRowId({ url: '', videoOrder, timeToComplete: 1 });
const toVideo = ({ rowId: _rowId, ...video }: VideoRow): Video => video;

export function CreateVideoForm() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['videoForm']);

  const currentTab = useAppSelector(selectCurrentTab);
  const lot = useAppSelector(selectLot);
  const editLot = useAppSelector(selectEditLot);

  const [videoData, setVideoData] = useState<VideoRow[]>(() => [emptyVideo(1)]);
  // Several uploads can run at once (one per row, video or captions): saving waits for all of them.
  const [runningUploads, setRunningUploads] = useState<number>(0);

  const isUploading = runningUploads > 0;

  useEffect(() => {
    const introLot: IntroLot | undefined = currentTab === Menus.CREATE ? lot : editLot;
    if (introLot?.videos && introLot.videos.length > 0) {
      setVideoData(introLot.videos.map(withRowId));
    }
  }, [currentTab, lot, editLot]);

  const addEmpty = () => {
    // Find the maximum videoOrder in the current videoData
    const maxOrder = videoData.reduce((max, video) => Math.max(max, video.videoOrder), 0);
    setVideoData([...videoData, emptyVideo(maxOrder + 1)]);
  };

  const removeRow = (rowId: number) => {
    // ReSet the order of the remaining videos to be consecutive
    setVideoData((previousVideoData) =>
      previousVideoData
        .filter((video) => video.rowId !== rowId)
        .map((video, videoIndex) => ({ ...video, videoOrder: videoIndex + 1 }))
    );
  };

  const editRow = (rowId: number, field: keyof Video, newValue: string | number) => {
    setVideoData((previousVideoData) =>
      previousVideoData.map((video) => (video.rowId === rowId ? { ...video, [field]: newValue } : video))
    );
  };

  // A row is valid once its file has been uploaded and its duration and order are filled in:
  // the state of the row is the only source of truth, there is no parallel validity array.
  const isRowValid = (video: Video) =>
    Boolean(video.url) && Number.isInteger(video.videoOrder) && video.videoOrder >= 1 && video.timeToComplete > 0;

  const notify = (type: NotificationType, message: string) => {
    dispatch(setNotificationType(type));
    dispatch(setNotificationMessage(message));
  };

  const saveVideoContent = async () => {
    if (isUploading) {
      notify('warning', t('uploadInProgress'));
      return;
    }
    if (!videoData.every(isRowValid)) {
      notify('error', t('invalidRows'));
      return;
    }

    const saved = await editIntroLotVideos(videoData.map(toVideo));
    if (!saved) {
      notify('error', t('saveError'));
      return;
    }

    // The api has deleted the replaced files: the lot kept in the store must reflect the saved
    // videos, otherwise coming back to this step would resubmit addresses that no longer exist.
    const savedFields = { videos: saved.videos ?? [], timeToComplete: saved.timeToComplete };
    if (currentTab === Menus.CREATE) {
      dispatch(setCreatedLot({ ...lot, ...savedFields }));
    } else {
      dispatch(setEditLot({ ...editLot, ...savedFields }));
    }

    const missingCaptions = videoData.filter((video) => !video.captionsUrl).length;
    if (missingCaptions > 0) {
      notify('warning', t('savedWithoutCaptions', { missing: missingCaptions }));
    } else {
      notify('validation', t('saved'));
    }
  };

  return (
    <>
      <div className="video-form__help">
        <p className="video-form__help-title">{t('helpTitle')}</p>
        <p>{t('helpFormat')}</p>
        <p>{t('helpYoutube')}</p>
        <p>{t('helpSrt')}</p>
      </div>
      <div className="video-form-list">
        {videoData.map((video) => (
          <VideoFormComponent
            key={video.rowId}
            isEdit={true}
            videoData={video}
            editRow={editRow}
            removeRow={removeRow}
            onUploadStart={() => setRunningUploads((uploads) => uploads + 1)}
            onUploadEnd={() => setRunningUploads((uploads) => Math.max(0, uploads - 1))}
          />
        ))}
      </div>
      <div className="create-card-form__buttons">
        <Button
          label={t('add')}
          translation=""
          type="button"
          classType={'secondary-button-lg'}
          onClick={addEmpty}
          disabled={isUploading}
        />
        <Button
          label={t('save')}
          translation=""
          type="button"
          classType={'primary-button-lg'}
          onClick={saveVideoContent}
          disabled={isUploading}
        />
      </div>
    </>
  );
}
